(() => {
  'use strict';

  if (customElements.get('quote-modal')) return;

  // La app puede tardar en un arranque en frío; el lead se guarda aunque el navegador corte antes.
  const SEND_TIMEOUT_MS = 25000;
  const SEND_ERROR = 'No hemos podido enviar tu solicitud. Vuelve a intentarlo en unos segundos o escríbenos por WhatsApp al 649 757 426.';

  class QuoteModal extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.openButton = this.querySelector('[data-quote-open]');
      this.closeButton = this.querySelector('[data-quote-close]');
      this.form = this.querySelector('form');
      this.steps = [...this.querySelectorAll('[data-quote-step]')];
      this.nextButton = this.querySelector('[data-quote-next]');
      this.backButton = this.querySelector('[data-quote-back]');
      this.submitButton = this.querySelector('.mp-quote__submit');
      this.progress = this.querySelector('[data-quote-progress]');
      this.context = this.querySelector('.mp-quote__context');
      this.body = this.querySelector('.mp-quote__body');
      this.actions = this.querySelector('.mp-quote__actions');
      this.successView = this.querySelector('[data-quote-success]');
      this.successTitle = this.successView?.querySelector('.mp-quote__success-title');
      this.restartButton = this.querySelector('[data-quote-restart]');
      this.successCloseButton = this.querySelector('[data-quote-success-close]');
      this.wizardRegions = [this.progress, this.context, this.body, this.actions];
      this.wizardLabelId = this.dialog?.getAttribute('aria-labelledby');
      this.wizardDescriptionId = this.dialog?.getAttribute('aria-describedby');
      this.currentStep = 0;
      this.isSubmitting = false;

      if (
        !this.dialog || !this.openButton || !this.closeButton || !this.form || !this.steps.length ||
        !this.successView || !this.successTitle || !this.restartButton || !this.successCloseButton ||
        this.wizardRegions.some((region) => !region)
      ) return;

      this.enhanceWizard();
      this.form.querySelector('[data-quote-quantity]')?.addEventListener('input', () => this.boxProposal());
      this.openButton.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => this.close());
      this.restartButton.addEventListener('click', () => this.restart());
      this.successCloseButton.addEventListener('click', () => this.close());
      this.nextButton.addEventListener('click', () => this.goForward());
      this.backButton.addEventListener('click', () => this.showStep(this.currentStep - 1, 'back'));
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => {
        window.MundoParquetScrollLock?.unlock(this);
        this.openButton.focus();
      });
      this.form.addEventListener('keydown', (event) => this.handleEnter(event));
      this.form.addEventListener('input', (event) => this.clearFieldError(event.target));
      this.form.addEventListener('change', (event) => {
        this.clearFieldError(event.target);
        this.maybeAdvanceOnChoice(event.target);
      });
      this.form.addEventListener('submit', (event) => this.handleSubmit(event));

      const params = new URLSearchParams(window.location.search);
      if (params.get('contact_posted') === 'true') this.open();
    }

    enhanceWizard() {
      // La validación propia evita que el navegador intente enfocar pasos ocultos.
      this.form.noValidate = true;
      this.dialog.classList.add('mp-quote--enhanced');

      // También cubre el éxito renderizado por Shopify si llega un envío nativo (sin JavaScript).
      if (!this.successView.hidden) {
        this.showSuccess(false);
        return;
      }

      this.progress.hidden = false;
      this.showStep(0, 'forward', false);
    }

    open() {
      if (!this.dialog.open) this.dialog.showModal();
      // El <dialog> atura el focus, però a l'iPhone no sempre atura la pàgina de sota.
      window.MundoParquetScrollLock?.lock(this);
      window.requestAnimationFrame(() => this.focusCurrentView());
    }

    close() {
      if (this.dialog.open) this.dialog.close();
    }

    disconnectedCallback() {
      window.clearTimeout(this.advanceTimer);
      window.MundoParquetScrollLock?.unlock(this);
    }

    showStep(index, direction = 'forward', moveFocus = true) {
      if (index < 0 || index >= this.steps.length) return;

      this.currentStep = index;
      this.dialog.dataset.direction = direction;
      this.steps.forEach((step, stepIndex) => {
        const isActive = stepIndex === index;
        step.classList.toggle('is-active', isActive);
        step.setAttribute('aria-hidden', String(!isActive));
      });

      const position = index + 1;
      const percent = Math.round((position / this.steps.length) * 100);
      this.querySelector('[data-quote-progress-text]').textContent = `Paso ${position} de ${this.steps.length}`;
      const stepCount = this.querySelector('[data-quote-step-count]');
      if (stepCount) stepCount.textContent = `Paso ${position} de ${this.steps.length}`;
      this.querySelector('[data-quote-progress-percent]').textContent = `${percent}%`;
      this.querySelector('[data-quote-progress-bar]').style.width = `${percent}%`;
      this.backButton.hidden = index === 0;
      this.nextButton.hidden = index === this.steps.length - 1;
      this.submitButton.hidden = index !== this.steps.length - 1;

      if (moveFocus) window.requestAnimationFrame(() => this.focusStepHeading());
    }

    focusStepHeading() {
      this.steps[this.currentStep]?.querySelector('.mp-quote__question-title')?.focus({ preventScroll: true });
    }

    focusCurrentView() {
      if (!this.successView.hidden) {
        this.successTitle.focus({ preventScroll: true });
        return;
      }

      this.focusStepHeading();
    }

    // Una pregunta de una sola elección avanza sola al marcarla: menos clics y sin perder el botón.
    maybeAdvanceOnChoice(target) {
      if (!this.dialog.classList.contains('mp-quote--enhanced')) return;
      if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
      const step = this.steps[this.currentStep];
      if (!step || !step.contains(target)) return;
      if (this.currentStep === this.steps.length - 1) return;
      const controls = step.querySelectorAll('input:not([type="radio"]), textarea, select');
      if (controls.length > 0) return;
      const names = new Set([...step.querySelectorAll('input[type="radio"]')].map((input) => input.name));
      if (names.size !== 1) return;
      const quick = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.clearTimeout(this.advanceTimer);
      this.advanceTimer = window.setTimeout(() => this.goForward(), quick ? 0 : 220);
    }

    goForward() {
      if (!this.validateStep(this.currentStep)) return;
      this.showStep(this.currentStep + 1, 'forward');
    }

    handleEnter(event) {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
      if (event.target.matches('textarea, button, a')) return;

      event.preventDefault();
      if (this.currentStep === this.steps.length - 1) {
        this.form.requestSubmit();
      } else {
        this.goForward();
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      const invalidStep = this.steps.findIndex((step, index) => !this.validateStep(index, false));

      if (invalidStep !== -1) {
        this.showStep(invalidStep, invalidStep < this.currentStep ? 'back' : 'forward', false);
        this.validateStep(invalidStep, true);
        return;
      }

      this.isSubmitting = true;
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');

      const idempotencyKey = this.retryKey(this.buildPayload(''));
      const endpoint = (this.dataset.endpoint || '').trim();

      // Solo la app recibe el presupuesto: guarda el lead y avisa a Carla. Nunca se envía
      // también el formulario nativo de Shopify (su correo va a info@ y duplicaba el aviso
      // cuando la app tardaba más que el límite del navegador aunque ya lo hubiera guardado).
      try {
        if (!endpoint) throw new Error('sin endpoint');
        const payload = this.buildPayload(idempotencyKey);
        const attribution = typeof window.mpAttribution === 'function' ? window.mpAttribution() : null;
        if (attribution) payload.attribution = attribution;
        const options = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-MP-Form-Secret': this.dataset.secret
          },
          // La clave de idempotencia viaja en el cuerpo (idempotency_key); no como cabecera,
          // para no ampliar Access-Control-Allow-Headers del endpoint (evita bloqueo CORS).
          body: JSON.stringify(payload)
        };
        let response = await this.fetchWithTimeout(endpoint, options);
        // Older app versions may reject the new field. Retry once, with the same
        // idempotency key, only after an HTTP rejection (never an ambiguous timeout).
        if (!response.ok && payload.attribution) {
          delete payload.attribution;
          response = await this.fetchWithTimeout(endpoint, { ...options, body: JSON.stringify(payload) });
        }
        if (!response.ok) throw new Error(`endpoint ${response.status}`);
        this.pendingRetry = null;
        this.showSuccess();
      } catch (_error) {
        this.showSendError();
      }
    }

    showSendError() {
      this.isSubmitting = false;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      const lastStep = this.steps.length - 1;
      if (this.currentStep !== lastStep) this.showStep(lastStep, 'forward', false);
      const error = this.steps[lastStep].querySelector('[data-quote-step-error]');
      if (error) error.textContent = SEND_ERROR;
    }

    validateStep(index, focusInvalid = true) {
      const step = this.steps[index];
      const error = step.querySelector('[data-quote-step-error]');
      const quantity = step.querySelector('[data-quote-quantity]');
      const customerTypes = [...step.querySelectorAll('[data-quote-customer-type]')];
      const fields = [...step.querySelectorAll('[data-quote-required]')];
      let invalidField = null;
      let message = '';

      this.clearStepErrors(step);

      if (customerTypes.length && !customerTypes.some((field) => field.checked)) {
        invalidField = customerTypes[0];
        message = 'Indícanos si eres particular o profesional';
      } else if (quantity && (quantity.value.trim() === '' || Number(quantity.value) <= 0)) {
        invalidField = quantity;
        message = this.dataset.unit === 'ml'
          ? 'Indica una longitud aproximada para poder continuar.'
          : 'Indica una superficie aproximada para poder continuar.';
      }

      for (const field of fields) {
        if (invalidField) break;
        const kind = field.dataset.quoteRequired;
        const empty = field.type === 'checkbox' ? !field.checked : field.value.trim() === '';

        if (empty) {
          invalidField = field;
          message = this.requiredMessage(kind);
        } else if (kind === 'email' && !field.validity.valid) {
          invalidField = field;
          message = 'Revisa el correo: parece que falta algo.';
        }
      }

      if (!invalidField) return true;

      if (invalidField.matches('[data-quote-customer-type]')) {
        customerTypes.forEach((field) => field.setAttribute('aria-invalid', 'true'));
      } else {
        invalidField.setAttribute('aria-invalid', 'true');
      }
      error.textContent = message;
      if (focusInvalid) invalidField.focus({ preventScroll: false });
      return false;
    }

    requiredMessage(kind) {
      const messages = {
        name: '¿Nos dices tu nombre para poder continuar?',
        population: 'Indica la población donde está el proyecto.',
        email: 'Necesitamos un correo donde enviarte el presupuesto.',
        phone: 'Déjanos un teléfono por si necesitamos confirmar algún detalle.',
        privacy: 'Para enviarlo, necesitamos que aceptes la política de privacidad.'
      };

      return messages[kind] || 'Completa este campo para poder continuar.';
    }

    clearFieldError(field) {
      const step = field.closest('[data-quote-step]');
      if (!step) return;
      if (field.matches('[data-quote-customer-type]')) {
        step.querySelectorAll('[data-quote-customer-type]').forEach((option) => option.removeAttribute('aria-invalid'));
      } else {
        field.removeAttribute('aria-invalid');
      }
      step.querySelector('[data-quote-step-error]').textContent = '';
    }

    clearStepErrors(step) {
      step.querySelectorAll('[aria-invalid="true"]').forEach((field) => field.removeAttribute('aria-invalid'));
      step.querySelector('[data-quote-step-error]').textContent = '';
    }

    boxProposal() {
      const size = Number(this.dataset.boxSize), unit = Number(this.dataset.unitSize);
      if (!(size > 1 && unit > 0)) return null;
      const requested = Number(this.form.querySelector('[data-quote-quantity]')?.value);
      const summary = this.querySelector('[data-quote-box-summary]');
      const field = this.querySelector('[data-quote-box-detail]');
      if (!(size > 1 && unit > 0 && requested > 0)) {
        if (summary) summary.hidden = true;
        if (field) field.value = '';
        return null;
      }
      const pieces = Math.ceil(requested / unit - 1e-9);
      const qty = Math.ceil(pieces / size) * size;
      const fmt = n => n.toLocaleString('es-ES', { maximumFractionDigits: 2 });
      const money = cents => (cents / 100).toLocaleString('es-ES', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      const cents = qty * Number(this.dataset.price);
      const tax = Number(this.dataset.taxPct);
      const grossPrimary = window.mpPriceMode === 'gross';
      const label = grossPrimary || this.dataset.taxesIncluded === 'true' ? 'IVA incl.' : '+ IVA';
      const detail = `${qty / size} caja${qty / size > 1 ? 's' : ''} · ${qty} tiras (${fmt(qty * unit)} ml reales)`;
      if (summary) {
        summary.hidden = false;
        summary.textContent = detail + (cents > 0 ? `: ${money(grossPrimary ? Math.round(cents * (100 + tax) / 100) : cents)} ${label}${tax > 0 ? (grossPrimary ? ` (${money(cents)} + IVA)` : ` (${money(Math.round(cents * (100 + tax) / 100))} IVA incl.)`) : ''}` : '');
      }
      if (field) field.value = `${detail} · para ${fmt(requested)} ml solicitados`;
      return { coverage: Math.round(qty * unit * 100) / 100, detail: field?.value || detail };
    }

    buildPayload(idempotencyKey) {
      const value = (name) => {
        const field = this.form.elements.namedItem(name);
        return field ? field.value : '';
      };
      const checked = (selector) => Boolean(this.form.querySelector(selector)?.checked);
      const quantityField = this.form.querySelector('[data-quote-quantity]');
      const parsedQuantity = quantityField && quantityField.value !== '' ? Number(quantityField.value) : null;
      const box = this.boxProposal();
      const quantity = box ? box.coverage : Number.isFinite(parsedQuantity) ? parsedQuantity : null;
      const unit = this.dataset.unit === 'm2' || this.dataset.unit === 'ml' ? this.dataset.unit : null;
      const selectedCustomerType = this.form.querySelector('[data-quote-customer-type]:checked')?.value || '';
      const customerType = ['particular', 'profesional'].includes(selectedCustomerType) ? selectedCustomerType : '';

      return {
        name: value('contact[name]'),
        email: value('contact[email]'),
        phone: value('contact[phone]'),
        population: value('contact[poblacion]'),
        message: [value('contact[body]'), this.dataset.variantTitle ? `Variante: ${this.dataset.variantTitle}${this.dataset.variantSku ? ` · Ref. ${this.dataset.variantSku}` : ''}` : '', box?.detail].filter(Boolean).join('\n'),
        customer_type: customerType,
        product_handle: this.dataset.productHandle,
        product_title: this.dataset.productTitle,
        unit,
        quantity,
        wants_skirting: checked('[data-quote-skirting]'),
        wants_underlay: checked('[data-quote-underlay]'),
        privacy_accepted: true,
        marketing_consent: checked('input[name="contact[marketing_consent]"]'),
        consent_text_version: value('consent_text_version'),
        source_page: window.location.pathname,
        website: value('contact[website]'),
        idempotency_key: idempotencyKey
      };
    }

    async fetchWithTimeout(url, options) {
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);

      try {
        return await fetch(url, { ...options, signal: controller.signal });
      } finally {
        window.clearTimeout(timeout);
      }
    }

    // Un reintento con los mismos datos reutiliza la clave: si el primer envío ya llegó,
    // la app lo reconoce y no vuelve a avisar a Carla. Datos distintos, clave nueva.
    retryKey(payload) {
      const signature = JSON.stringify(payload);
      if (this.pendingRetry?.signature !== signature) {
        this.pendingRetry = { signature, key: this.createIdempotencyKey() };
      }
      return this.pendingRetry.key;
    }

    createIdempotencyKey() {
      if (window.crypto?.randomUUID) return window.crypto.randomUUID();

      return `mp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }

    showSuccess(moveFocus = true) {
      this.isSubmitting = false;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      this.dialog.classList.add('is-success');
      this.wizardRegions.forEach((region) => {
        region.hidden = true;
        region.setAttribute('aria-hidden', 'true');
      });
      this.successView.hidden = false;
      this.dialog.setAttribute('aria-labelledby', this.successTitle.id);
      this.dialog.setAttribute('aria-describedby', `${this.successTitle.id}-description`);

      if (moveFocus) window.requestAnimationFrame(() => this.successTitle.focus({ preventScroll: true }));
    }

    restart() {
      this.isSubmitting = false;
      this.pendingRetry = null;
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
      this.form.reset();
      this.boxProposal();
      [...this.form.elements].forEach((field) => {
        if (field.type === 'hidden' || field.type === 'submit' || field.type === 'button') return;

        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = false;
        } else {
          field.value = '';
        }

        field.removeAttribute('aria-invalid');
      });
      this.steps.forEach((step) => this.clearStepErrors(step));

      this.successView.hidden = true;
      this.dialog.classList.remove('is-success');
      this.wizardRegions.forEach((region) => {
        region.hidden = false;
        region.removeAttribute('aria-hidden');
      });
      this.dialog.setAttribute('aria-labelledby', this.wizardLabelId);
      this.dialog.setAttribute('aria-describedby', this.wizardDescriptionId);
      this.showStep(0, 'back');
    }
  }

  customElements.define('quote-modal', QuoteModal);
})();
